import { gql } from "@apollo/client";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import {
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
  GridSortDirection,
  GridSortModel,
} from "@mui/x-data-grid";
import { WidgetHeader } from "components/atoms/WidgetHeader";
import { WidgetPaper } from "components/atoms/WidgetPaper";
import { CloudDataGrid } from "components/modules/CloudDataGrid";
import { CSVDisinfectionDownloader } from "components/modules/CSVDisinfectionDownloader";
import { useDDFilter } from "components/modules/disinfectionFilter/DisinfectionFilterPanel";
import { DisinfectedCell } from "components/modules/tableCells/DisinfectedCell";
import { DisinfectionModeCell } from "components/modules/tableCells/DisinfectionModeCell";
import { RobotCell } from "components/modules/tableCells/RobotCell";
import { UVDRobotStatusIconCell } from "components/modules/tableCells/UVDRobotStatusIconCell";
import { format } from "date-fns";
import { da } from "date-fns/locale";
import { graphql } from "gql";
import {
  DisinfectionFilter,
  DisinfectionSort,
  ResultsPerPage,
} from "gql/graphql";
import _ from "lodash";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import {
  usePrefetchWithSnack,
  useQueryWithSnack,
} from "utils/useQueryWithSnack";
import { secondsToHMS } from "utils/utilFunctions";
import { getUVDSerialNumber } from "utils/UVD/getUVDRobotSerialNumber";
import { getRoom } from "utils/UVD/UVDUtils";
import { DisinfectionReportDialog } from "../disinfectionReportWidgets/DisinfectionReportDialog";

const columns: GridColDef[] = [
  {
    field: "status",
    headerName: "",
    width: 20,
    sortable: false,
    renderCell: (params) => <UVDRobotStatusIconCell status={params.value} />,
  },
  {
    field: "start",
    headerName: "Date",
    headerAlign: "right",
    flex: 1,
    maxWidth: 150,
    align: "right",
    type: "date",
    sortable: true,
    renderCell: (params: GridRenderCellParams) =>
      params.value !== undefined
        ? format(new Date(params.value), "dd LLL yyyy")
        : "",
    valueGetter: (params) => new Date(params.value),
  },
  {
    field: "startedBy",
    headerName: "Started by operator",
    flex: 1,
    maxWidth: 300,
    sortable: true,
    renderCell: (params: GridRenderCellParams) => (
      <Typography fontWeight={500}>{params.value}</Typography>
    ),
  },
  {
    field: "room",
    headerName: "Room",
    flex: 1,
    maxWidth: 300,
    sortable: true,
    renderCell: (params: GridRenderCellParams) =>
      getRoom(params.row.room, params.row.department),
  },
  {
    field: "disinfected",
    headerName: "Disinfected",
    flex: 1,
    maxWidth: 100,
    sortable: false,
    headerAlign: "right",
    align: "right",
    renderCell: (params) => <DisinfectedCell disinfection={params.row} />,
  },
  {
    field: "start-finish",
    headerName: "Start - Finish",
    flex: 1,
    maxWidth: 150,
    headerAlign: "right",
    align: "right",
    type: "dateTime",
    sortable: false,
    renderCell: (params: GridRenderCellParams) =>
      params.row?.start === undefined && params.row?.end === undefined
        ? ""
        : [
            format(new Date(params.row.start), "p", { locale: da }),
            format(new Date(params.row.end), "p", { locale: da }),
          ].join(" - "),
  },
  {
    field: "uvcLightDuration",
    headerName: "UV-C Duration",
    flex: 1,
    maxWidth: 150,
    headerAlign: "right",
    align: "right",
    sortable: true,
    renderCell: (params: GridRenderCellParams) =>
      params.row?.start === undefined && params.row?.end === undefined
        ? ""
        : secondsToHMS(params.value),
  },
  {
    field: "type",
    headerName: "Mode",
    flex: 1,
    maxWidth: 150,
    sortable: true,
    renderCell: (params) => <DisinfectionModeCell type={params.row.type} />,
  },
  {
    field: "robot",
    headerName: "Robot",
    flex: 1,
    maxWidth: 350,
    sortable: true,
    renderCell: (params: GridRenderCellParams) => {
      // .status is used both for report status and robot status.
      // Pass the right one to the robot cell
      const modifiedRow = JSON.parse(JSON.stringify(params.row));
      modifiedRow.status = modifiedRow.robotStatus;

      return (
        <RobotCell
          serialNumber={params.row.robotId}
          robot={modifiedRow}
          showStatusDot
          showOrg
        />
      );
    },
    valueGetter: (params) =>
      params.row?.robot?.name ||
      getUVDSerialNumber(params.row?.robotId) ||
      undefined,
  },
];

const QUERY = graphql(`
  query DisinfectionReports(
    $filter: DisinfectionFilter!
    $pagination: Pagination
    $sortBy: DisinfectionSort
  ) {
    disinfections(filter: $filter, pagination: $pagination, sortBy: $sortBy) {
      disinfections {
        id
        schemaVersion
        robotId
        robot {
          id
          robot {
            serialNumber
          }
          active {
            serialNumber
          }
          organization {
            id
            name
          }
        }
        robotId
        department
        distanceTraveled
        end
        interruptions {
          cause
          buttonFunction
          buttonReset
          connectionClosed
          connectionUnstable
          countdownDelayTooLong
          detectionBodyHeat
          detectionWalk
          errorGeneral
          errorLidarBlocked
          lampsOff
          localizationLoss
          uiInactivity
          uiMovement
          uiPermission
          uiUserTriggerOthers
          uiUserTriggerWarmUp
          warningLowBattery
          unsafeTimeout
          personDetection
        }
        room
        start
        startedBy
        status
        submittedBy
        type
        uvcLightDuration
        positions {
          _id
          name
          status
        }
        navigationMap {
          id
          active
          area
          mapItems {
            id
            name
            position {
              x
              y
              yaw
            }
            category
          }
          origin {
            x
            y
            yaw
          }
          resolution
          size {
            height
            width
          }
          name
          navigationMapFile {
            id
            name
          }
          zones {
            id
            name
            category
            mapItems {
              id
              category
              name
              order
              position {
                x
                y
                yaw
              }
              time
            }
          }
        }
        heatMap {
          id
          name
        }
        events {
          commentUsername
          id
          time
          type
          username
          commentMessage
          commentTime
        }
      }
      count
    }
  }
`);

export const ROBOTS_SPECIFIC_INFO = gql(`
  query RobotsSpecificInfo($organizationIds: [String!]) {
    robots(isActive: true, organizationIds: $organizationIds) {
      robots {
        serialNumber
        active {
          nickname
        }
        ... on UvdRobot {
          status {
            lastTimeActive
          }
          version {
            plc {
              hasNewAvailable
            }
            batteries {
              family
              version
            }
          }
        }
      }
    }
  }
`);

interface Props {
  hiddenFilter?: DisinfectionFilter;
}

export const DisinfectionsReportsTable = ({ hiddenFilter }: Props) => {
  const prevFilterParams = useRef();
  const [filterParams] = useDDFilter();
  const theme = useTheme();
  const largeScreen = useMediaQuery(theme.breakpoints.up("xl"));
  const prefetch = usePrefetchWithSnack(QUERY);

  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [reportOpen, setReportOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<any>(null);

  const pagesToPrefetch = 5; // Bidirectional pagination
  const pageSize = 10;

  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const getSortByEnum = (
    field: string,
    sort: GridSortDirection,
  ): DisinfectionSort => {
    const sortMap: Record<string, [DisinfectionSort, DisinfectionSort]> = {
      start: [DisinfectionSort.Start, DisinfectionSort.StartDesc],
      startedBy: [DisinfectionSort.StartedBy, DisinfectionSort.StartedByDesc],
      room: [DisinfectionSort.Room, DisinfectionSort.RoomDesc],
      uvcLightDuration: [
        DisinfectionSort.UvcLightDuration,
        DisinfectionSort.UvcLightDurationDesc,
      ],
      type: [DisinfectionSort.Type, DisinfectionSort.TypeDesc],
      robot: [DisinfectionSort.Robot, DisinfectionSort.RobotDesc],
    };

    const sortPair = sortMap[field];
    if (!sortPair || !sort) return DisinfectionSort.StartDesc; // Fallback to default
    return sort === "asc" ? sortPair[0] : sortPair[1];
  };
  useEffect(() => {
    setPage(0);
  }, [sortModel]);

  const { data, loading } = useQueryWithSnack(QUERY, {
    variables: {
      filter: {
        ...filterParams,
        ...hiddenFilter,
      },
      pagination: {
        resultsPerPage: ResultsPerPage.ResultsPerPage10,
        page: page,
      },
      sortBy:
        sortModel.length > 0
          ? getSortByEnum(sortModel[0].field, sortModel[0].sort)
          : DisinfectionSort.StartDesc, // Fallback to default
    },
  });

  const combinedOrgIds = (hiddenFilter?.organizationIds || []).concat(
    filterParams?.organizationIds || [],
  );
  const { data: robotsSpecificInfoData } = useQueryWithSnack(
    ROBOTS_SPECIFIC_INFO,
    {
      variables: {
        ...(combinedOrgIds.length > 0 && { organizationIds: combinedOrgIds }),
      },
      fetchPolicy: "network-only",
      pollInterval: 10000,
    },
  );

  useEffect(() => {
    if (data?.disinfections.disinfections) {
      setTotalCount(data?.disinfections.count);
    }
  }, [data]);

  useEffect(() => {
    const debouncedPrefetch = _.debounce(() => {
      const formattedFilter = {
        ...filterParams,
        ...hiddenFilter,
      };

      // Prefetch pages close to current
      for (let i = Math.max(page - 5, 0); i <= page + pagesToPrefetch; i++) {
        prefetch({
          filter: formattedFilter,
          pagination: {
            resultsPerPage: ResultsPerPage.ResultsPerPage10,
            page: i,
          },
          sortBy:
            sortModel.length > 0
              ? getSortByEnum(sortModel[0].field, sortModel[0].sort)
              : DisinfectionSort.StartDesc, // Fallback to default
        });
      }

      // Prefetch last page
      prefetch({
        filter: formattedFilter,
        pagination: {
          resultsPerPage: ResultsPerPage.ResultsPerPage10,
          page: Math.max(
            Math.ceil((data?.disinfections.count ?? 0) / pageSize - 1),
            0,
          ),
        },
        sortBy:
          sortModel.length > 0
            ? getSortByEnum(sortModel[0].field, sortModel[0].sort)
            : DisinfectionSort.StartDesc, // Fallback to default
      });
    }, 200); // Wait 200ms between calls

    debouncedPrefetch();

    return () => {
      debouncedPrefetch.cancel();
    };
  }, [page, prefetch, sortModel]);

  useEffect(() => {
    prevFilterParams.current = filterParams;
  }, []);

  useEffect(() => {
    if (!_.isEqual(filterParams, prevFilterParams.current)) {
      setPage(0);
    }
    prevFilterParams.current = filterParams;
  }, [filterParams]);

  const handleOpenReport = (params: GridRowParams) => {
    setSelectedReport(params.row);
    setReportOpen(true);
  };

  const rows =
    data?.disinfections.disinfections.map((disinfection) => {
      const robotInfo = robotsSpecificInfoData?.robots?.robots?.find(
        (robot: any) =>
          robot.serialNumber === getUVDSerialNumber(disinfection.robotId),
      );

      return {
        ...disinfection,
        active: {
          nickname: robotInfo?.active?.nickname,
        },
        robotStatus: {
          lastTimeActive: robotInfo?.status?.lastTimeActive,
        },
        room: disinfection.room === "" ? "Current location" : disinfection.room,
        version: {
          plc: {
            hasNewAvailable: robotInfo?.version?.plc?.hasNewAvailable,
          },
          batteries:
            robotInfo?.version?.batteries?.map((battery: any) => ({
              family: battery.family,
              version: battery.version,
            })) ?? [],
        },
      };
    }) ?? [];

  return (
    <>
      <WidgetPaper>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <WidgetHeader
            label={<FormattedMessage id="reports" defaultMessage="Reports" />}
          />

          <CSVDisinfectionDownloader
            disabled={
              loading || (data?.disinfections.disinfections ?? []).length === 0
            }
            filters={{
              ...filterParams,
              ...hiddenFilter,
            }}
          />
        </Box>
        <CloudDataGrid
          initialState={{
            columns: {
              columnVisibilityModel: {
                startedBy: largeScreen,
                "start-finish": largeScreen,
                uvcLightDuration: largeScreen,
              },
            },
          }}
          rows={rows ?? []}
          columns={columns}
          onRowClick={handleOpenReport}
          page={page}
          pageSize={pageSize}
          onPageChanged={(pageNumber) => {
            setPage(pageNumber);
          }}
          loading={loading}
          rowCount={totalCount}
          paginationMode="server"
          sortModel={sortModel}
          onSortModelChange={(model: GridSortModel) => setSortModel(model)}
          sortingMode="server"
          hideFooter={false}
        />
      </WidgetPaper>
      {selectedReport ? (
        <DisinfectionReportDialog
          open={reportOpen}
          onClose={() => setReportOpen(false)}
          selectedReport={selectedReport}
        />
      ) : null}
    </>
  );
};
